import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: object;
}

const SITE_NAME = 'Radium';
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://radium.love';
const DEFAULT_DESCRIPTION =
  'The AI-first personalization framework for in-product decisioning. Ranked, sequenced, in-session experiences on the surfaces you own.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og/og-default.png`;

export function SEO({
  title,
  description,
  canonical,
  ogImage,
  noIndex,
  jsonLd,
}: SEOProps) {
  const pageTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  const desc = description ?? DEFAULT_DESCRIPTION;
  const img = ogImage ?? DEFAULT_OG_IMAGE;
  const canonicalUrl = canonical ?? undefined;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export const ORGANIZATION_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Radium',
  url: 'https://radium.love',
  logo: 'https://radium.love/og/og-default.png',
  sameAs: ['https://unicorn.love'],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'hello@radium.love',
    contactType: 'sales',
  },
};

export function articleLd(opts: {
  headline: string;
  description: string;
  url: string;
  datePublished?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    url: opts.url,
    publisher: {
      '@type': 'Organization',
      name: 'Radium',
      url: 'https://radium.love',
    },
    datePublished: opts.datePublished ?? '2026-01-01',
  };
}
